import {
  Fragment,
  ReactNode,
  Key,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import { styled } from '@mui/material';
import { Virtuoso } from 'react-virtuoso';

import {
  Skeleton,
  SxProps,
  withProps,
  VirtuosoScroller,
  mergeSx,
  Stack,
} from '@cast/design-system';
import { sort } from '@cast/utils';

import { useListHeight } from './_hooks/useListHeight';

import { getNoDataState, useSearchContext } from '@cast/shared-search-ui';

const OVERFLOW_X_HIDDEN_SELECTOR =
  '[data-overlayscrollbars-viewport~=overflowXHidden]';
const OVERFLOW_Y_HIDDEN_SELECTOR =
  '[data-overlayscrollbars-viewport~=overflowYHidden]';
const OVERFLOW_Y_SCROLL_SELECTOR =
  '[data-overlayscrollbars-viewport~=overflowYScroll]';

const HORIZONTAL_OFFSET = 16;
const OVERSCAN_OFFSET = 500;

const ListContentStack = styled(Stack, { target: 'SearchableList-Content' })({
  gap: 8,
});

const Scroller = withProps(VirtuosoScroller, {
  sx: {
    [`&[data-overlayscrollbars]:has(${OVERFLOW_X_HIDDEN_SELECTOR}), ${OVERFLOW_Y_HIDDEN_SELECTOR}${OVERFLOW_X_HIDDEN_SELECTOR}`]:
      {
        overflow: 'clip !important',
        overflowClipMargin: 16,
      },
    [OVERFLOW_Y_SCROLL_SELECTOR]: {
      transform: `translateX(-${HORIZONTAL_OFFSET}px)`,
      '> div': {
        pl: HORIZONTAL_OFFSET,
      },
    },
    [OVERFLOW_X_HIDDEN_SELECTOR]: {
      mr: `${-HORIZONTAL_OFFSET}px !important`,
      '> div': {
        pr: HORIZONTAL_OFFSET,
      },
    },
  },
});

type Props<T> = {
  itemKey: keyof T | ((item: T) => Key);
  renderItem: (item: T, index: number) => ReactNode;
  outerHeader?: ReactNode;
  components?: {
    noData?: ReactNode;
    noResults?: ReactNode;
    failed?: ReactNode;
  };
  loadingProps?: {
    skeleton?: ReactNode;
    skeletonCount?: number;
  };
  containerSx?: SxProps;
  /**
   * How many items should be rendered before virtualization is turned on.
   * Set to `0` to always virtualize. Default is `100`.
   */
  virtualizationThreshold?: number;
  /**
   * Set to 'fill-parent' to fill the available space in the container, or to a fixed value.
   */
  height?: string | number;
  /**
   * When height="fill-parent", the list will fill the available space but not shrink below this height.
   * Otherwise, the list will shrink as small as the content down to this height.
   */
  minHeight?: number | string;
  /**
   * When height="fill-parent", the list will fill the available space but not grow above this height.
   * Otherwise, the list will grow as tall as the content up to this height.
   */
  maxHeight?: string | number;
  testId?: string;
  sx?: SxProps;
};

export const SearchableList = <T,>({
  itemKey,
  renderItem,
  outerHeader,
  components,
  loadingProps,
  containerSx,
  virtualizationThreshold = 100,
  height,
  minHeight,
  maxHeight,
  testId,
  sx,
}: Props<T>) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [totalListHeight, setTotalListHeight] = useState(0);

  const virtuosoHeight = useListHeight(rootRef, headerRef, {
    totalListHeight,
    minHeight,
    maxHeight,
    height,
  });

  const [stableVirtuosoHeight, setStableVirtuosoHeight] = useState<
    number | null
  >(virtuosoHeight);

  if (virtuosoHeight !== null && virtuosoHeight !== stableVirtuosoHeight) {
    setStableVirtuosoHeight(virtuosoHeight);
  }

  const itemContent = useCallback(
    (index: number, item: T) => renderItem(item, index),
    [renderItem]
  );

  const computeItemKey = useCallback(
    (index: number, item: T) =>
      (typeof itemKey === 'function'
        ? itemKey(item)
        : (item[itemKey] as Key)) ?? index,
    [itemKey]
  );

  const sxRef = useRef(sx);
  sxRef.current = sx;

  const ListComponent = useMemo(
    () =>
      // eslint-disable-next-line react/display-name
      (props: React.HTMLAttributes<HTMLDivElement>) =>
        <ListContentStack {...props} sx={sxRef.current} />,
    []
  );

  const ctx = useSearchContext();
  if (!ctx) {
    throw new Error('SearchableList has to be used in SearchContext');
  }

  const data = useMemo(() => ctx.filteredData ?? [], [ctx.filteredData]);

  const shouldVirtualise = data.length > virtualizationThreshold;
  const overscan = shouldVirtualise
    ? (virtuosoHeight ?? 0) + OVERSCAN_OFFSET
    : Number.MAX_SAFE_INTEGER;

  const { serverSideSort, sortingState, setSortFn } = ctx;
  const { accessor, direction, sortingType } = sortingState?.sortBy || {};

  const sortFn = useCallback(
    <T,>(data: T[]) =>
      accessor && direction
        ? sort(data, { accessor, direction, sortingType })
        : data,
    [accessor, direction, sortingType]
  );

  useEffect(() => {
    if (!serverSideSort) {
      setSortFn?.(sortFn);
    }
  }, [setSortFn, sortFn, serverSideSort]);

  const { component: noDataComponent } = getNoDataState(ctx, {
    components,
  });

  const { skeleton, skeletonCount = 5 } = loadingProps || {
    skeleton: <Skeleton height={64} />,
  };

  if (noDataComponent) {
    return (
      <Stack sx={containerSx} data-testid={testId}>
        {noDataComponent}
      </Stack>
    );
  }

  return (
    <Stack
      ref={rootRef}
      sx={mergeSx(
        { height: height === 'fill-parent' ? '100%' : undefined },
        containerSx
      )}
      data-testid={testId}
    >
      <div ref={headerRef}>{outerHeader}</div>
      {ctx.isLoading ? (
        <ListContentStack>
          {[...Array(skeletonCount)].map((_, index) => (
            <Fragment key={index}>{skeleton}</Fragment>
          ))}
        </ListContentStack>
      ) : stableVirtuosoHeight === null ? null : (
        <Virtuoso<T>
          data={data}
          style={{
            height: stableVirtuosoHeight,
          }}
          totalListHeightChanged={setTotalListHeight}
          overscan={{
            main: overscan,
            reverse: overscan,
          }}
          itemContent={itemContent}
          computeItemKey={computeItemKey}
          components={{
            Scroller,
            List: ListComponent,
          }}
        />
      )}
    </Stack>
  );
};
